import React, { useMemo } from 'react';
import { UserEditedMeta } from './PatientProfileInformation';
// @ts-ignore
import styles from '../PatientProfileV2.module.scss';
import FlyerPrintButtonAfterRegistration from '../../../Modules/EArchive/Components/FlyerPrint/FlyerPrintButton/FlyerPrintButtonAfterRegistration';
import LinkButton from '../../Button/LinkButton';
import { Theme } from '../../../Enums/Theme.enum';
import Text from '../../Base/Text/Text';
import apiClient from '../../../Services/ApiClient';
import { useAppContext } from '../../../Services/AppContext';
import useApiClient from '../../../Modules/Base/Hooks/useApiClient';
import useNotification from '../../../Hooks/useNotification.hook';
import useTranslation from '../../../Hooks/useTranslation';
import {
	useLazyWorkplaceMemberIdQuery,
	UserInvitation,
} from '../../../graphql/graphql';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Modules/Base/Components/FormattedMomentDate/FormattedMomentDate';
import { getMomentDate } from '../../../Utils/dates.util';
import NoEscape from '../../NoEscape';

interface Props {
	user?: UserEditedMeta;
	childId?: string;
	invitations?: UserInvitation[];
}

const PatientProfileInvitations: React.FC<Props> = ({
	user,
	childId,
	invitations,
}) => {
	const { workplaceId, dateFormatMoment } = useAppContext();
	const { displayError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const [getWorkplaceMemberId] = useLazyWorkplaceMemberIdQuery();
	const id = user?.id || childId;

	const handleSendInvitation = async () => {
		if (id) {
			const { workplaceMember } = await getWorkplaceMemberId({
				userId: id,
				workplaceId: workplaceId,
			}).unwrap();

			try {
				const result = await apiClient.put(
					'/workplaces/' +
						workplaceId +
						'/patient/' +
						workplaceMember?.id +
						'/invitation',
					{},
				);
				if (result.status === 200) {
					showNotificationSuccess({
						content: t('component.patientProfileInvitations.sentToPatient'),
					});
				}
			} catch (e: any) {
				displayError(e);
			}
		}
	};

	const getCodeString = (label: string, code: string, date: string) => {
		if (!date) {
			return code;
		}
		return `${code} <span>(${t(label)} ${getMomentDate(date).format(
			dateFormatMoment,
		)})</span>`;
	};

	const invitationsFinal = useMemo(() => {
		const array: string[] = [];

		const data: UserInvitation[] =
			(invitations && invitations.length > 0
				? invitations
				: (user?.invitations as UserInvitation[])) ?? [];

		if (data.length) {
			data.forEach(({ code, used, createdAt, declinedAt }: any) => {
				if (!used) {
					let invitation = getCodeString(
						'component.patientProfileInvitations.send',
						code,
						createdAt,
					);
					if (declinedAt) {
						invitation = getCodeString(
							'component.patientProfileInvitations.declined',
							code,
							declinedAt,
						);
					}
					array.push(invitation);
				}
			});
		}

		return array;
	}, [user]);

	if (invitationsFinal.length < 1) {
		return <></>;
	}

	return (
		<div className={styles.item}>
			<Text
				className={styles.name}
				tx={childId ? 'base.code' : 'base.invitationCode'}
				firstUpper
				colon
			/>
			{invitationsFinal.map((item, key) => {
				return (
					<div className={styles.code} key={key}>
						<NoEscape>{item}</NoEscape>
					</div>
				);
			})}
			{id && (
				<>
					<LinkButton
						title='base.sendCode'
						theme={Theme.BLUE}
						onClick={handleSendInvitation}
						className={'mr-3'}
					/>
					<FlyerPrintButtonAfterRegistration
						patientId={id}
						buttonTitle='base.print'
					/>
				</>
			)}
		</div>
	);
};

export default PatientProfileInvitations;
